import { Component } from "react";

/**
 * ErrorBoundary — captura erros de renderização/lifecycle dos componentes filhos
 * e exibe uma interface de fallback amigável no tema do sistema.
 * Evita "tela preta" quando ocorre uma exceção não tratada.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Erro capturado:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || String(this.state.error) || "Erro desconhecido";
    const stack = this.state.errorInfo?.componentStack || this.state.error?.stack || "";

    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex items-center justify-center p-6 bg-[#0F172A] text-[#E9EEF5]"
      >
        <div className="max-w-2xl w-full rounded-lg border border-[#EF4444]/40 bg-[#1E293B] shadow-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#334155] bg-[#EF4444]/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#EF4444]/20 flex items-center justify-center text-[#EF4444]">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#EF4444] font-semibold">
                  Erro na aplicação
                </div>
                <div className="font-heading text-xl mt-0.5">Algo deu errado ao renderizar esta tela</div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#94A3B8] mb-2">Mensagem do erro</div>
              <div
                data-testid="error-boundary-message"
                className="rounded-md bg-[#0F172A] border border-[#334155] p-3 text-sm text-[#FCA5A5] font-mono break-words"
              >
                {message}
              </div>
            </div>

            {stack && (
              <details className="text-xs text-[#94A3B8]">
                <summary className="cursor-pointer hover:text-[#E9EEF5] uppercase tracking-widest font-semibold">
                  Detalhes técnicos (stack)
                </summary>
                <pre className="mt-2 rounded-md bg-[#0F172A] border border-[#334155] p-3 overflow-x-auto max-h-64 text-[10px] whitespace-pre-wrap">
{stack}
                </pre>
              </details>
            )}

            <div className="rounded-md bg-[#F59E0B]/5 border border-[#F59E0B]/30 p-3 text-xs text-[#FCD34D]">
              A aplicação continua ativa. Tente recarregar a página ou voltar à tela anterior.
              Se o erro persistir, contate o administrador do sistema.
            </div>

            <div className="flex gap-2 pt-1">
              <button
                data-testid="error-boundary-reload"
                onClick={this.handleReload}
                className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm"
              >
                Recarregar página
              </button>
              <button
                data-testid="error-boundary-back"
                onClick={() => window.history.back()}
                className="px-5 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm"
              >
                Voltar
              </button>
              <button
                data-testid="error-boundary-reset"
                onClick={this.handleReset}
                className="px-5 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm text-[#94A3B8]"
              >
                Tentar renderizar novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
